import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useCMSPage } from "../../../context/CMSContext";
import { useCMSGlobal } from "../../../hooks/useCMSField";

interface HeroSectionProps {
  onBookNow: () => void;
}

const DEFAULT_STATS = [
  { val: "2,000+",    label: "5-star reviews",      icon: "ri-star-fill",      color: "text-cl-lime-bold" },
  { val: ">95%",      label: "Recycled or donated",  icon: "ri-recycle-line",   color: "text-cl-green" },
  { val: "W. Midlands",label: "Coverage area",       icon: "ri-map-pin-2-line", color: "text-cl-green" },
  { val: "Same day",  label: "Available slots",      icon: "ri-flashlight-line",color: "text-cl-lime-bold" },
];

export default function HeroSection({ onBookNow }: HeroSectionProps) {
  const [postcode, setPostcode] = useState("");
  const [visible, setVisible] = useState(false);

  const { data: homeCMS } = useCMSPage("home");
  const globalCMS = useCMSGlobal();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hero: any = (homeCMS as any)?.hero ?? {};
  const stats: typeof DEFAULT_STATS = (hero?.stats?.length ? hero.stats : DEFAULT_STATS).map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (s: any, i: number) => ({ ...DEFAULT_STATS[i], ...s })
  );

  const headline         = hero?.headline         ?? "Your rubbish\ngone today,\ndone properly.";
  const trustpilotScore  = hero?.trustpilot_score ?? "4.8";
  const trustpilotLabel  = hero?.trustpilot_label ?? "2,000+ Trustpilot reviews";
  const ctaLabel         = hero?.cta_label        ?? "Get a Free Quote";
  const postcardPH       = hero?.postcode_placeholder ?? "Enter your postcode";
  const phone            = globalCMS?.phone       ?? "03335 775445";
  const whatsapp         = globalCMS?.whatsapp_number ?? "443335775445";

  // Man & Van price is live from WC — kept hardcoded in hero as CMS value
  const manVanPrice = hero?.man_van_price ?? "£75";

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <section className="relative w-full bg-white overflow-hidden pt-20">
      <div
        className="absolute top-0 right-0 w-1/2 h-full pointer-events-none"
        style={{
          background: "linear-gradient(135deg, #0d2d6e 0%, #09245b 60%, #071a45 100%)",
          clipPath: "polygon(18% 0, 100% 0, 100% 100%, 0% 100%)",
        }}
      />

      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row items-center gap-0 min-h-[88vh] lg:min-h-[640px] py-14 lg:py-0">

          {/* ── LEFT COLUMN ── */}
          <div
            className={`flex-1 max-w-2xl flex flex-col justify-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
          >
            {/* Trustpilot badge */}
            <div className="flex items-center gap-2.5 mb-7">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#00b67a] text-sm"></i>
                ))}
              </div>
              <span className="text-cl-dark font-bold text-sm">{trustpilotScore}</span>
              <span className="w-px h-4 bg-gray-300"></span>
              <span className="text-gray-500 text-sm">{trustpilotLabel}</span>
            </div>

            {/* Headline — CMS-driven, supports newlines */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-cl-dark leading-[1.05] mb-4 tracking-tight">
              {headline.split("\n").map((line: string, i: number) => (
                <span key={i}>
                  {i === 1 ? <span className="text-cl-green">{line}</span> : line}
                  {i < headline.split("\n").length - 1 && <br />}
                </span>
              ))}
            </h1>

            {/* Trust sub-bullets */}
            <div className="flex flex-col gap-1.5 mb-6">
              {[
                { icon: "ri-flashlight-line",   text: "Same day rubbish removal across the West Midlands" },
                { icon: "ri-shield-check-line", text: "Fully licensed & insured" },
                { icon: "ri-government-line",   text: "Environment Agency Licensed Carrier" },
              ].map((t) => (
                <span key={t.text} className="flex items-center gap-2">
                  <i className={`${t.icon} text-cl-green text-sm flex-shrink-0`}></i>
                  <span className="text-gray-600 text-sm font-bold">{t.text}</span>
                </span>
              ))}
            </div>

            {/* 3-point CTA strip */}
            <div className="flex flex-wrap items-center gap-2 mb-7">
              {[
                { step: "1", label: "SEND PICTURE", icon: "ri-camera-line" },
                { step: "2", label: "GET A PRICE",  icon: "ri-price-tag-3-line" },
                { step: "3", label: "WE CLEAR IT",  icon: "ri-truck-line" },
              ].map((item, i) => (
                <div key={item.step} className="flex items-center gap-2">
                  <div className="flex items-center gap-2 bg-cl-lime border-2 border-cl-dark rounded-full px-4 py-2">
                    <i className={`${item.icon} text-cl-dark text-sm`}></i>
                    <span className="text-cl-dark font-black text-xs whitespace-nowrap">{item.label}</span>
                  </div>
                  {i < 2 && <i className="ri-arrow-right-line text-gray-300 text-sm"></i>}
                </div>
              ))}
            </div>

            {/* Postcode CTA row */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6 max-w-md">
              <div className="flex-1 relative">
                <i className="ri-map-pin-2-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base z-10"></i>
                <input
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder={postcardPH}
                  className="w-full bg-white border border-gray-200 rounded-xl pl-11 pr-4 py-4 text-sm text-cl-dark placeholder-gray-400 focus:outline-none focus:border-cl-green focus:ring-2 focus:ring-cl-green/10 transition-all"
                  style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                />
              </div>
              <button
                onClick={onBookNow}
                className="bg-cl-green hover:bg-green-700 text-white font-black px-7 py-4 rounded-xl whitespace-nowrap text-sm transition-all duration-200 cursor-pointer"
                style={{ boxShadow: "0 4px 16px rgba(22,163,74,0.3)" }}
              >
                {ctaLabel}
              </button>
            </div>

            {/* WhatsApp instant quote CTA */}
            <a
              href={`https://wa.me/${whatsapp}?text=Hi%20Cleario%21%20I%27d%20like%20an%20instant%20quote.%20Here%20are%20my%20items%3A`}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-cl-tint border-2 border-[#25D366]/40 hover:border-[#25D366] rounded-xl px-5 py-3 mb-5 transition-all cursor-pointer group max-w-md"
            >
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#25D366] flex-shrink-0">
                <i className="ri-whatsapp-line text-white text-base"></i>
              </span>
              <div>
                <p className="text-cl-dark font-black text-sm leading-none">Get an instant quote via WhatsApp</p>
                <p className="text-gray-500 text-xs mt-0.5 font-medium">Send a photo or describe your items</p>
              </div>
              <i className="ri-arrow-right-line text-[#25D366] ml-auto group-hover:translate-x-1 transition-transform"></i>
            </a>

            <p className="text-sm text-gray-700 font-bold mb-8 flex items-center gap-1.5">
              <i className="ri-shield-check-line text-cl-green text-base"></i>
              No credit card required &nbsp;·&nbsp; Free no-obligation quote &nbsp;·&nbsp; Fully licensed &amp; insured
            </p>

            {/* USP pills */}
            <div className="flex flex-wrap gap-2 mb-10">
              {[
                { icon: "ri-flashlight-line",   label: "Same-day slots" },
                { icon: "ri-leaf-line",          label: ">95% recycled" },
                { icon: "ri-shield-check-line",  label: "Fully licensed" },
                { icon: "ri-phone-fill",         label: phone },
              ].map((u) => (
                <div key={u.label} className={`flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 ${u.icon === "ri-phone-fill" ? "bg-cl-tint border-cl-green/30" : ""}`}>
                  <i className={`${u.icon} text-cl-green text-sm`}></i>
                  <span className="text-cl-dark font-bold text-sm">{u.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div
            className={`hidden lg:flex flex-col gap-4 w-[420px] xl:w-[460px] flex-shrink-0 pl-10 justify-center transition-all duration-700 delay-150 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <div className="relative rounded-2xl overflow-hidden" style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.12)" }}>
              <img
                src="https://public.readdy.ai/ai/img_res/edited_e7431d0492d4d3c1b81c1cbf2f06e868_d11e671b.jpg"
                alt="Cleario van ready for collection"
                className="w-full h-72 xl:h-80 object-contain object-center bg-white"
              />
              <div
                className="absolute bottom-4 left-4 bg-white rounded-xl px-4 py-3 flex items-center gap-3"
                style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.12)" }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-cl-tint">
                  <i className="ri-truck-line text-cl-green text-lg"></i>
                </div>
                <div>
                  <p className="text-gray-400 text-xs font-medium">Man &amp; Van from</p>
                  <p className="text-cl-dark font-black text-lg leading-none">{manVanPrice}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white rounded-xl p-4 border border-gray-100 flex items-center gap-3"
                  style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-cl-tint flex-shrink-0">
                    <i className={`${stat.icon} ${stat.color} text-base`}></i>
                  </div>
                  <div>
                    <p className="text-cl-dark font-black text-base leading-none">{stat.val}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      <div className="w-full h-px bg-gray-100"></div>

      {/* Trust strip */}
      <div className="bg-cl-dark w-full">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-[#00b67a] text-xs"></i>
              ))}
            </div>
            <span className="text-white text-xs font-bold">
              Excellent {trustpilotScore} — <span className="text-white/60 font-normal">{trustpilotLabel}</span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 -ml-24">
            {[
              { icon: "ri-government-line",   text: "EA Licensed waste carrier" },
              { icon: "ri-shield-check-line", text: "Fully Licensed & Insured" },
              { icon: "ri-timer-flash-line",  text: "Same-day availability" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-1.5">
                <i className={`${item.icon} text-cl-lime-bold text-sm`}></i>
                <span className="text-white/70 text-xs font-medium whitespace-nowrap">{item.text}</span>
              </div>
            ))}
          </div>
          <Link
            to="/book"
            className="bg-cl-lime-bold hover:bg-yellow-400 text-cl-dark font-black text-xs px-5 py-2.5 rounded-lg whitespace-nowrap transition-colors cursor-pointer"
          >
            Book Now →
          </Link>
        </div>
      </div>
    </section>
  );
}
